import { notEmpty } from '@cyren/common-lib';
import { uniq } from 'lodash';
import { useMemo } from 'react';
import { useSurveyMetaControllerFindBySurvey } from '../api/generated/survey-meta/survey-meta';

type SurveyMetaQnaItem = {
  id?: string;
  qnaQuestionKey: string;
  qnaAnswerKey: string;
};

type SurveyMetaQnaMetadata = {
  items?: SurveyMetaQnaItem[];
  [key: string]: unknown;
};

export function useSurveyMeta({
  metaKey,
  surveyId,
}: {
  metaKey: string;
  surveyId?: string | number | null;
  surveyShortId?: string | null;
}) {
  const parsedSurveyId = Number(surveyId);
  const hasValidSurveyId = Number.isFinite(parsedSurveyId) && parsedSurveyId > 0;

  const {
    data: v2MetaData,
    isLoading: loading,
  } = useSurveyMetaControllerFindBySurvey(
    {
      surveyId: parsedSurveyId,
      metaKey,
    },
    {
      query: {
        enabled: hasValidSurveyId,
      },
    },
  );

  const metaItems = useMemo<SurveyMetaQnaItem[]>(() => {
    const metadata = (v2MetaData?.metadata || {}) as SurveyMetaQnaMetadata;
    const { items } = metadata;
    if (!Array.isArray(items)) return [];

    return items.filter(
      (item): item is SurveyMetaQnaItem =>
        !!item &&
        typeof item.qnaQuestionKey === 'string' &&
        typeof item.qnaAnswerKey === 'string',
    );
  }, [v2MetaData?.metadata]);

  const transKeys = useMemo(() => {
    const keys = metaItems
      .flatMap((item) => [item.qnaQuestionKey, item.qnaAnswerKey])
      .filter(notEmpty);
    return uniq(keys);
  }, [metaItems]);

  const meta =
    v2MetaData && {
      id: `${v2MetaData.id}`,
      attributes: {
        metaKey: v2MetaData.metaKey,
        metaType: v2MetaData.metaType,
        metadata: v2MetaData.metadata,
      },
    };

  return {
    meta,
    data: undefined,
    loading,
    transKeys,
    metaItems,
  } as const;
}
